/**
 * #824 — event-pipe liveness from the web client.
 *
 * The daemon treats any IPC as liveness and uses a 180s dead gate. The JS
 * 15s timer heartbeat is still useful, but macOS backgrounds throttle it to
 * ~60s. Visibility / pageshow pings prove the pipe without waiting on that timer.
 */

export const EVENT_PIPE_HEARTBEAT_MESSAGE = 'event_pipe_heartbeat';

type TauriCore = {
  invoke: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
};

function tauriCore(): TauriCore | null {
  try {
    const core = (window as unknown as { __TAURI__?: { core?: TauriCore } }).__TAURI__
      ?.core;
    if (!core || typeof core.invoke !== 'function') return null;
    return core;
  } catch {
    return null;
  }
}

/** Fire-and-forget JS→daemon pipe ping. Never throws. */
export function pingEventPipe(reason: string): void {
  try {
    const core = tauriCore();
    if (!core) return;
    void core
      .invoke('diagnostic_log_js', {
        level: 'info',
        category: 'pipe',
        message: EVENT_PIPE_HEARTBEAT_MESSAGE,
        data: { reason },
      })
      .catch(() => {});
  } catch {
    /* never propagate */
  }
}

/** Install visibility/pageshow pings. Returns an uninstall function. */
export function installEventPipeLiveness(
  doc: Document = document,
  win: Window = window,
): () => void {
  const onVisibility = () => {
    if (doc.visibilityState === 'visible') {
      pingEventPipe('visibility_resume');
    }
  };
  const onPageShow = () => pingEventPipe('pageshow');
  doc.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('pageshow', onPageShow);
  pingEventPipe('install');
  return () => {
    doc.removeEventListener('visibilitychange', onVisibility);
    win.removeEventListener('pageshow', onPageShow);
  };
}
